import { Routes, Route } from 'react-router-dom';
import { ArcheLayout } from '../../../components/ArcheLayout';
import { SocialMediaHeader } from '../components/SocialMediaHeader';
import { DashboardPage } from './DashboardPage';
import { AccountsPage } from './AccountsPage';
import { ComposerPage } from './ComposerPage';
import { BrandSettingsPage } from './BrandSettingsPage';
import { WriterTablePage } from './WriterTablePage';
import { InspirationPage } from './InspirationPage';
import { AnalyticsPage } from './AnalyticsPage';
import { FiasAIProvider, useFiasAIContext, FiasAIPopup } from '@/components/fias-ai';
import type { ArcheAICapabilities } from '@/components/fias-ai';
import { FEATURE_FLAGS } from '../feature-flags';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { SocialMediaTourModal } from '../components/SocialMediaTourModal';
import '../../../../index.css';

const ARCHE_ID = 'arche_social_media';

const capabilities: ArcheAICapabilities = {
  archeId: ARCHE_ID,
  enabled: true,
  archeDescription:
    'Social media management arche for composing, scheduling, and publishing posts across platforms.',
  actions: [],
  getStateSnapshot: () => ({}),
  applyAction: () => {},
};

function FiasAIPopupConnected() {
  const ctx = useFiasAIContext();
  return (
    <FiasAIPopup
      isOpen={ctx.isPanelOpen}
      onClose={ctx.closePanel}
      messages={ctx.messages}
      isStreaming={ctx.isStreaming}
      onSubmit={ctx.submit}
      onClear={ctx.reset}
      onApplyProposal={ctx.applyProposal}
      onRejectProposal={ctx.rejectProposal}
    />
  );
}

/**
 * Arche wrapper for Social Media — provides ArcheLayout with sidebar
 * and handles sub-routing for all social media pages.
 */
export function SocialMediaArchePage() {
  const [settings, updateSettings] = useArcheSettings('arche_social_media', {
    hasSeenTour: false,
  });

  return (
    <ArcheLayout>
      <FiasAIProvider capabilities={capabilities}>
        <div className="flex flex-col h-full min-w-0 w-full">
          <div className="relative">
            <SocialMediaHeader />
            <div
              className="pointer-events-none absolute -bottom-6 left-0 right-0 h-8 z-40"
              style={{
                background:
                  'linear-gradient(to bottom, var(--social-bg), rgba(var(--social-bg-rgb), 0))',
              }}
            />
          </div>

          <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
            <Routes>
              <Route path="/overview" element={<DashboardPage />} />
              <Route path="/accounts" element={<AccountsPage />} />
              <Route path="/" element={<ComposerPage />} />
              <Route path="/compose/:postId" element={<ComposerPage />} />
              <Route path="/settings" element={<BrandSettingsPage />} />
              <Route path="/write" element={<WriterTablePage />} />
              <Route path="/inspiration" element={<InspirationPage />} />
              {FEATURE_FLAGS.socialMediaAnalytics && (
                <Route path="/analytics" element={<AnalyticsPage />} />
              )}
            </Routes>
          </div>
          <FiasAIPopupConnected />
        </div>
        {!settings.hasSeenTour && (
          <SocialMediaTourModal onDismiss={() => updateSettings({ hasSeenTour: true })} />
        )}
      </FiasAIProvider>
    </ArcheLayout>
  );
}
